import { createConfigParametersRepository } from '../../configParametersRepository';
import { checkThirdPartyTrackingStorageInit } from '../../configParametersRepository/storageClient/index';
import {
  DATALAYER_STORAGE_KEY,
  STORAGE_KEY,
  TC_VARS,
} from '../resources/constants';
import type { StorageTrackingData } from '../types';
import type { Datalayer } from './types';

/**
 * Creates a datalayer storage client for managing datalayer and session storage operations.
 * @returns {object} - An object containing methods to interact with the datalayer and session storage.
 */
export const datalayerStorageClient = () => {
  const { getTrackingStorageClient } = createConfigParametersRepository();

  /**
   * Updates the datalayer stored in the tracking storage client by merging the existing data
   * with the provided partial datalayer object.
   *
   * @param datalayer - A partial object of type `Datalayer` containing the properties to update
   * in the existing datalayer. Only the provided properties will be merged into the existing data.
   *
   * @remarks
   * Do not store product related params using this function
   */
  const updateDatalayerGlobalParams = (datalayer: Partial<Datalayer>) => {
    checkThirdPartyTrackingStorageInit();
    const { update } = getTrackingStorageClient();
    update(DATALAYER_STORAGE_KEY, (existingData = {}) => {
      return { ...existingData, ...datalayer };
    });
  };

  const getSessionStorageItem = <T>(
    key: string,
    fallbackValue: T = {} as T
  ): T => {
    checkThirdPartyTrackingStorageInit();
    const { getFromStorage } = getTrackingStorageClient();
    return getFromStorage<T>('sessionStorage', key, fallbackValue);
  };

  const setSessionStorageItem = <T>(key: string, value: T) => {
    checkThirdPartyTrackingStorageInit();
    const { setInStorage } = getTrackingStorageClient();
    setInStorage('sessionStorage', key, value);
  };

  /**
   * Retrieves the current stored datalayer.
   * @returns {Partial<Datalayer>|undefined} - The current datalayer or undefined if not available.
   */
  const getDatalayerGlobalParams = (): Partial<Datalayer> | undefined => {
    checkThirdPartyTrackingStorageInit();
    const storageTrackingData: StorageTrackingData =
      getSessionStorageItem(STORAGE_KEY);
    return storageTrackingData?.[DATALAYER_STORAGE_KEY];
  };

  /**
   * Retrieves the previous state of the datalayer by merging its historical states.
   *
   * @returns {Partial<Datalayer>} A partial representation of the datalayer,
   * combining all historical states. If no history exists, returns an empty object.
   */
  const getPreviousDatalayer = (): Partial<Datalayer> => {
    return getSessionStorageItem(TC_VARS, {}) || {};
  };

  /**
   * Pushes a new datalayer object into the datalayer history stored in session storage.
   *
   * @param datalayer - A partial representation of the `Datalayer` object to be added to the history.
   *                     This allows adding only specific properties of the `Datalayer`.
   *
   * The function retrieves the current datalayer history from session storage, appends the new
   * datalayer object to the history, and then updates the session storage with the new history.
   */
  const storePreviousDatalayer = (datalayer: Partial<Datalayer>) => {
    const previous = getPreviousDatalayer();
    setSessionStorageItem(TC_VARS, { ...previous, ...datalayer });
  };

  /**
   * Retrieves the Google Analytics client ID from session storage.
   * @returns the Google Analytics client ID stored in session storage
   */
  const getClientId = (): string => {
    checkThirdPartyTrackingStorageInit();
    const storageTrackingData: StorageTrackingData =
      getSessionStorageItem(STORAGE_KEY);
    return storageTrackingData?.userProperties?.client_id || 'not-initialized';
  };

  /**
   * Determines if the app traffic is from a native interface.
   * @returns A string representation of a boolean indicating if the traffic is from a native interface.
   */
  const getAppTraffic = (): string => {
    const { getGenericParameters } = createConfigParametersRepository();
    const { isNativeInterface } = getGenericParameters();
    return String(isNativeInterface);
  };

  return {
    getDatalayerGlobalParams,
    getPreviousDatalayer,
    storePreviousDatalayer,
    updateDatalayerGlobalParams,
    getClientId,
    getAppTraffic,
  };
};
